/**
 * Shared i18n module.
 *
 * Single source of truth for all user-facing strings across every
 * NovixOne surface (web dashboard, mobile app, and future admin
 * tools). Both apps pull from the same `Dictionary` shape, so adding
 * a key fails at compile time on every platform until it's filled
 * in for every locale — nothing drifts silently.
 *
 * Adding a new string:
 *   1. Add the key to `Dictionary` in `./types.ts`
 *   2. Fill it in for all four locales under `./dictionaries/`
 *   3. Consume via `useDict()` in whichever app needs it
 *
 * Adding a new locale:
 *   1. Extend `locales` / `localeNames` in `./config.ts`
 *   2. Create `./dictionaries/<code>.ts` and fill in every key
 *   3. Extend `getDictionary()` below
 */
import en from './dictionaries/en'
import zhHans from './dictionaries/zh-Hans'
import zhHant from './dictionaries/zh-Hant'

import { defaultLocale, type Locale } from './config'
import type { Dictionary } from './types'

export type { Dictionary } from './types'
export { locales, localeNames, defaultLocale, isValidLocale } from './config'
export type { Locale } from './config'

/**
 * Synchronous dictionary lookup. Mobile uses this directly (all
 * dictionaries are bundled — total small enough that lazy loading
 * adds more complexity than it saves). Web wraps this in a dynamic
 * import in its React context for now, but could switch to the sync
 * path here if bundle size isn't a concern.
 */
export function getDictionary(locale: Locale): Dictionary {
  switch (locale) {
    case 'zh-Hant':
      return zhHant
    case 'zh-Hans':
      return zhHans
    case 'en':
      return en
    default:
      return getDictionary(defaultLocale)
  }
}

/**
 * Simple string interpolation: t("Hello {name}", { name: "Alex" })
 *
 * Kept deliberately minimal — no plural / gender / select support.
 * For plural forms prefer `Intl.PluralRules` + branching on a dict
 * key (see `datetime.ts` in the mobile app for a worked example).
 */
export function t(
  template: string,
  vars?: Record<string, string | number>,
): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    key in vars ? String(vars[key]) : `{${key}}`,
  )
}
